import type { TUseGetSocialApplyByStatusResponse } from "@api";
import { useGetSocialApplyByStatus, useGetSocialOfferApplyCount } from "@api";
import { SocialApplyStatus } from "@bbj-types/socialApply/enum";
import type { ObjectId } from "@bbj-types/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { formatFilters, useCandidateFilters } from "./filters";

export type TSocialOfferTab =
  | SocialApplyStatus.PRESCRIPTION_FILLED
  | SocialApplyStatus.INTEGRATED_CANDIDATE
  | SocialApplyStatus.REFUSED_STRUCTURE;

const isSocialOfferTab = (value: string | null): value is TSocialOfferTab => {
  return Object.values(SocialApplyStatus).includes(value as SocialApplyStatus);
};

const isSocialApplyData = (value: unknown): value is TUseGetSocialApplyByStatusResponse<TSocialOfferTab>["data"] => {
  return Array.isArray(value);
};

export const useSocialOffersDetailsBody = (offerId: string) => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [{ data: applyCount }, getApplyCount] = useGetSocialOfferApplyCount();
  const [{ data: applyData, loading }, getApplyData] = useGetSocialApplyByStatus();
  const [applies, setApplies] = useState<TUseGetSocialApplyByStatusResponse<TSocialOfferTab>["data"]>();
  const [socialApplySelected, setSocialApplySelected] = useState<ObjectId[]>([]);
  const { filters, dispatch } = useCandidateFilters();

  const changeTab = (tab: SocialApplyStatus) => {
    navigate(`${pathname}?tab=${tab}`);
  };

  const selectedTab = useMemo(() => {
    const param = new URLSearchParams(search).get("tab");
    if (!isSocialOfferTab(param)) {
      return SocialApplyStatus.PRESCRIPTION_FILLED;
    }

    return param;
  }, [search]);

  const refetchApplies = useCallback(() => {
    setApplies(undefined);
    getApplyCount(offerId);
    getApplyData(offerId, selectedTab, { filters: formatFilters(filters), skip: 0, limit: 50 });
  }, [offerId, selectedTab, filters, getApplyCount, getApplyData]);

  useEffect(() => {
    refetchApplies();
  }, [refetchApplies]);

  useEffect(() => {
    if (applyData) {
      setApplies((prev) => {
        const newAppliesArray = [...(prev || []), ...applyData.data];
        if (isSocialApplyData(newAppliesArray)) {
          return newAppliesArray;
        }
        return applyData.data;
      });
    }
  }, [applyData]);

  return {
    changeTab,
    selectedTab,
    applyCount,
    applies,
    setApplies,
    loading,
    refetchApplies,
    filters,
    dispatch,
    socialApplySelected,
    setSocialApplySelected,
  };
};
